/*
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc.
 * All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

import React, { lazy } from 'react';
import { inject, observer } from 'mobx-react';
import { Redirect, Route, Switch, useLocation } from 'react-router-dom';
import { AuthenticatedTemplate } from '@azure/msal-react';
import { Container } from '@material-ui/core';
import SuspenseWrapper from '../../common/components/SuspenseWrapper';
import { AI_ADMIN_PATH } from '../../common/constants';

import Header from '../../common/header/Header';
import GuidedStudyTestbedConfigure from './components/testbed/GuidedStudyTestbedConfigure';
import GuidedStudyTestbedLauncher from './components/testbed/GuidedStudyTestbedLauncher';

const StudioGraphComponent = lazy(() =>
  import(/* webpackChunkName: "stdo" */ './components/studio/StudioGraphComponent')
);
const TestbedConfigure = lazy(() => import(/* webpackChunkName: "tstbd-c" */ './components/testbed/TestbedConfigure'));
const TestbedLauncher = lazy(() => import(/* webpackChunkName: "tstbd-l" */ './components/testbed/TestbedLauncher'));
const QuizAET = lazy(() => import(/* webpackChunkName: "tstbd-q" */ './components/testbed/QuizAET'));

const StudioContainer = (props) => {
  const { pathname } = useLocation();
  const isStudioRoute = /\/berlin\/[^/]+\/studio(\/|$)/.test(pathname);

  const mainStyle = {
    width: '100%',
    margin: '4.8rem auto 0',
    boxSizing: 'border-box',
    ...(isStudioRoute
      ? {
          height: 'calc(100vh - 4.8rem)',
          overflow: 'hidden'
        }
      : {
          overflowX: 'auto',
          height: 'auto'
        })
  };

  return (
    <>
      <Header />
      <main style={mainStyle}>
        <AuthenticatedTemplate>
          <SuspenseWrapper>
            <Switch>
              {/* Classic studio routes — single Route (graph path first) so opening a graph does not remount Studio */}
              <Route
                path={['/berlin/:tenantname/studio/graph/:graphId/:version', '/berlin/:tenantname/studio']}
                exact
                render={() => <StudioGraphComponent {...props} />}
              />

              <Route path="/berlin/:tenantName/quizaet" exact render={() => <QuizAET {...props} />} />

              <Route
                path="/berlin/testbed"
                exact
                render={() => (
                  <Container maxWidth="xl">
                    <TestbedConfigure {...props} />
                  </Container>
                )}
              />
              <Route
                path="/berlin/testbed/:tenantName/launch"
                exact
                render={() => (
                  <Container maxWidth="xl">
                    <TestbedLauncher {...props} />
                  </Container>
                )}
              />

              <Route
                path="/berlin/guided-study-testbed"
                exact
                render={() => (
                  <Container maxWidth="xl">
                    <GuidedStudyTestbedConfigure {...props} />
                  </Container>
                )}
              />
              <Route
                path="/berlin/guided-study-testbed/:tenantName/launch"
                exact
                render={() => (
                  <Container maxWidth="xl">
                    <GuidedStudyTestbedLauncher {...props} />
                  </Container>
                )}
              />

              <Route render={() => <Redirect to={AI_ADMIN_PATH} />} />
            </Switch>
          </SuspenseWrapper>
        </AuthenticatedTemplate>
      </main>
    </>
  );
};

export default inject()(observer(StudioContainer));
